import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpenCheck,
  CalendarDays,
  Cpu,
  GraduationCap,
  Laptop,
  MapPin,
  School,
  Users,
  Wifi,
} from "lucide-react";
import bg1 from "../assets/optimized/hero-1.jpg";
import bg2 from "../assets/optimized/hero-2.jpg";
import bg3 from "../assets/optimized/hero-3.jpg";
import bg4 from "../assets/optimized/hero-4.jpg";
import bg5 from "../assets/optimized/hero-5.jpg";
import careerImage from "../assets/optimized/career.jpg";
import eventImage1 from "../assets/optimized/event-1.jpg";
import eventImage2 from "../assets/optimized/event-2.jpg";
import eventImage3 from "../assets/optimized/event-3.jpg";
import techImage from "../assets/optimized/technology.jpg";
import pypLogo from "../assets/PYP-Eng-logo-522x522.jpg";
import dpLogo from "../assets/new-DP-logo-355x358.jpg";
import continuumLogo from "../assets/ib-world-school-continuum.png";
import { fetchEvents } from "../lib/api";
import { SITE, cn, whatsappLink } from "../lib/site";
import { schoolTimeline } from "./Timeline";
import Testimonial from "../componentsM/Testimonial";
import Map from "../componentsM/Map";
import { WhatsAppIcon } from "../componentsM/WhatsAppButton";
import { SectionHeading } from "../alumni/AlumniPages";

const SLIDES = [bg1, bg2, bg3, bg4, bg5];

const STATS = [
  { value: "1996", label: "Educating since" },
  { value: "3", label: "IB programmes: PYP, MYP and DP" },
  { value: "100%", label: "University placement" },
  { value: "40+", label: "Clubs and societies" },
];

function HeroSlider() {
  const [index, setIndex] = useState(0);
  // Only download a slide shortly before it is shown.
  const [ready, setReady] = useState(() => new Set([0]));
  useEffect(() => {
    const warm = setTimeout(() => setReady((r) => new Set([...r, 1])), 2500);
    const t = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), 6500);
    return () => {
      clearTimeout(warm);
      clearInterval(t);
    };
  }, []);
  useEffect(() => {
    setReady((r) => (r.has(index) && r.has((index + 1) % SLIDES.length) ? r : new Set([...r, index, (index + 1) % SLIDES.length])));
  }, [index]);
  return (
    <section className="relative isolate flex min-h-[78vh] items-end overflow-hidden bg-navy-950 text-white md:min-h-[86vh] md:items-center">
      {SLIDES.map((src, i) =>
        ready.has(i) ? (
        <img
          key={src}
          src={src}
          alt=""
          aria-hidden
          className={cn(
            "absolute inset-0 -z-20 h-full w-full object-cover transition-opacity duration-[1500ms]",
            i === index ? "opacity-100 animate-ken-burns" : "opacity-0"
          )}
        />
        ) : null
      )}
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-navy-950 via-navy-950/60 to-navy-950/10 md:bg-gradient-to-r md:from-navy-950/95 md:via-navy-900/70 md:to-transparent" />
      <div className="mx-auto w-full max-w-7xl px-4 pb-16 pt-24 sm:px-6 md:py-24">
        <div className="max-w-2xl animate-fade-up">
          <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-1.5 text-sm font-semibold text-gold-300 ring-1 ring-white/20 backdrop-blur">
            <GraduationCap className="h-4 w-4" aria-hidden /> Karachi &middot; Since 1996
          </p>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.1] sm:text-5xl md:text-6xl">
            First IB World <span className="text-gold-400">Continuum School</span> in Pakistan
          </h1>
          <p className="mt-5 max-w-xl text-lg text-navy-50/90 md:text-xl">
            Inquiring, caring young people prepared for the world's best universities, now with a Matriculation division too.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/admissions" className="inline-flex items-center gap-2 rounded-xl bg-gold-400 px-6 py-3.5 font-bold text-navy-900 shadow-lg transition hover:bg-gold-300">
              Apply for admission <ArrowRight className="h-5 w-5" aria-hidden />
            </Link>
            <a href="#programmes" className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-6 py-3.5 font-bold text-white ring-1 ring-white/30 backdrop-blur transition hover:bg-white/20">
              Explore programmes
            </a>
          </div>
          <p className="mt-6 text-sm text-navy-100/90">
            <em>"Education is the silent force."</em>
          </p>
        </div>
      </div>
      <div className="absolute bottom-6 right-6 hidden gap-2 md:flex">
        {SLIDES.map((src, i) => (
          <button
            key={src}
            onClick={() => setIndex(i)}
            className={cn("h-1.5 rounded-full transition-all", i === index ? "w-8 bg-gold-400" : "w-3 bg-white/50 hover:bg-white")}
            aria-label={`Show photo ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

function QuickLinks() {
  const items = [
    { icon: School, title: "IB Continuum", text: "PYP, MYP and Diploma under one roof", to: "/INTERNATIONAL-BACCALAUREATE" },
    { icon: BookOpenCheck, title: "Matriculation", text: "Our new national-curriculum division", to: "/matriculation" },
    { icon: CalendarDays, title: "Admissions open", text: "Apply online, track every step", to: "/admissions" },
    { icon: Users, title: "Parent portal", text: "Documents, forms and updates", to: "/admissions/login" },
  ];
  return (
    <section className="relative z-10 -mt-10 px-4 sm:px-6">
      <div className="mx-auto grid max-w-7xl gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {items.map(({ icon: Icon, title, text, to }) => (
          <Link key={title} to={to} className="group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-lift ring-1 ring-slate-100 transition hover:-translate-y-0.5">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-navy-50 text-navy-700 transition group-hover:bg-gold-400 group-hover:text-navy-900">
              <Icon className="h-6 w-6" aria-hidden />
            </div>
            <div className="min-w-0">
              <p className="font-display font-bold text-navy-800">{title}</p>
              <p className="text-sm text-slate-600">{text}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Programmes() {
  return (
    <section id="programmes" className="scroll-mt-28 bg-white py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeading
          eyebrow="Two pathways, one TIS"
          title="A programme for every learner"
          text="Families choose between the full IB Continuum and our Matriculation division. Both share the same campus, values and caring teachers."
        />
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <article className="flex flex-col overflow-hidden rounded-3xl bg-navy-800 text-white shadow-lift">
            <div className="flex items-center gap-4 border-b border-white/10 p-8">
              <img src={continuumLogo} alt="IB World School continuum" className="h-16 w-16 rounded-xl bg-white object-contain p-1" />
              <div>
                <p className="text-sm font-bold uppercase tracking-wider text-gold-400">International Baccalaureate</p>
                <h3 className="font-display text-2xl font-extrabold">IB Continuum</h3>
              </div>
            </div>
            <div className="grid flex-1 gap-4 p-8 sm:grid-cols-3">
              {[
                ["Primary Years", "Play group to Year 5", "/PrimaryYearsProgram", pypLogo],
                ["Middle Years", "Years 6 to 10", "/MYP", null],
                ["Diploma", "Years 11 and 12", "/IBDiploma", dpLogo],
              ].map(([title, ages, to, img]) => (
                <Link key={title} to={to} className="group rounded-2xl bg-white/5 p-4 ring-1 ring-white/10 transition hover:bg-white/10">
                  {img ? (
                    <img src={img} alt="" className="mb-3 h-10 w-10 rounded-lg bg-white object-contain" />
                  ) : (
                    <div className="mb-3 grid h-10 w-10 place-items-center rounded-lg bg-gold-400 font-display text-sm font-extrabold text-navy-900">MYP</div>
                  )}
                  <p className="font-display font-bold">{title}</p>
                  <p className="text-sm text-navy-100">{ages}</p>
                  <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-gold-300 group-hover:gap-2">
                    Learn more <ArrowRight className="h-4 w-4" aria-hidden />
                  </span>
                </Link>
              ))}
            </div>
            <p className="px-8 pb-8 text-navy-100">
              TIS was the first school in Pakistan authorised to offer all three IB programmes, giving children one continuous,
              inquiry-based education from their first day to university.
            </p>
          </article>

          <article className="relative flex flex-col overflow-hidden rounded-3xl bg-gold-50 ring-1 ring-gold-200">
            <span className="absolute right-6 top-6 rounded-full bg-gold-400 px-3 py-1 text-xs font-bold uppercase text-navy-900">New division</span>
            <div className="flex items-center gap-4 border-b border-gold-200 p-8">
              <div className="grid h-16 w-16 place-items-center rounded-xl bg-navy-800 text-gold-400">
                <BookOpenCheck className="h-8 w-8" aria-hidden />
              </div>
              <div>
                <p className="text-sm font-bold uppercase tracking-wider text-gold-700">National curriculum</p>
                <h3 className="font-display text-2xl font-extrabold text-navy-800">Matriculation Division</h3>
              </div>
            </div>
            <div className="flex flex-1 flex-col p-8">
              <p className="text-slate-700">
                The TIS Matriculation division brings the school's teaching standards, facilities and values to families who
                prefer the national curriculum leading to the Secondary School Certificate (Matric).
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="rounded-2xl bg-white p-4 ring-1 ring-gold-200">
                  <p className="font-display text-3xl font-extrabold text-navy-800">800+</p>
                  <p className="text-sm text-slate-600">students already enrolled</p>
                </div>
                <div className="rounded-2xl bg-white p-4 ring-1 ring-gold-200">
                  <p className="font-display text-3xl font-extrabold text-navy-800">SSC</p>
                  <p className="text-sm text-slate-600">Matric certification pathway</p>
                </div>
              </div>
              <div className="mt-auto flex flex-wrap gap-3 pt-8">
                <Link to="/matriculation" className="inline-flex items-center gap-2 rounded-xl bg-navy-800 px-5 py-3 font-bold text-white hover:bg-navy-900">
                  About Matriculation <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
                <Link to="/admissions" className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 font-bold text-navy-800 ring-1 ring-slate-300 hover:bg-slate-50">
                  Apply now
                </Link>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section className="bg-navy-900 py-16 text-white">
      <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
        {STATS.map((s) => (
          <div key={s.label} className="border-l-4 border-gold-400 pl-5">
            <p className="font-display text-4xl font-extrabold md:text-5xl">{s.value}</p>
            <p className="mt-1 text-navy-100">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Technology() {
  return (
    <section className="bg-slate-50 py-20 md:py-24">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <div className="relative">
          <img src={techImage} alt="Students learning with technology at TIS" className="aspect-[4/3] w-full rounded-3xl object-cover shadow-lift" loading="lazy" />
          <div className="absolute -bottom-6 -right-2 hidden rounded-2xl bg-gold-400 p-5 text-navy-900 shadow-lift sm:block">
            <p className="font-display text-3xl font-extrabold">1:1</p>
            <p className="text-sm font-semibold">laptop per child from age 10</p>
          </div>
        </div>
        <div>
          <SectionHeading align="left" eyebrow="Learning with technology" title="Technology is part of our community" />
          <p className="mt-5 text-lg text-slate-600">
            Our campuses have fibre-optic internet and multimedia systems in every room, and every student from age 10 learns
            with their own laptop.
          </p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-3">
            {[
              [Wifi, "Fibre-optic internet"],
              [Cpu, "Multimedia classrooms"],
              [Laptop, "One laptop per child"],
            ].map(([Icon, label]) => (
              <li key={label} className="flex items-center gap-2 rounded-xl bg-white p-3 text-sm font-semibold text-navy-800 ring-1 ring-slate-200">
                <Icon className="h-5 w-5 text-gold-600" aria-hidden /> {label}
              </li>
            ))}
          </ul>
          <Link to="/educational-technology" className="mt-8 inline-flex items-center gap-2 font-bold text-navy-700 hover:gap-3">
            More about educational technology <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </div>
    </section>
  );
}

function History() {
  const scroller = useRef(null);
  const recent = schoolTimeline.slice(-8).reverse();
  return (
    <section className="bg-white py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading align="left" eyebrow="Our story" title="Nearly three decades of firsts" />
          <Link to="/Tishistory" className="inline-flex items-center gap-2 font-bold text-navy-700 hover:gap-3">
            Full TIS history <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
        <div ref={scroller} className="mt-10 flex snap-x gap-5 overflow-x-auto pb-4">
          {recent.map((item) => (
            <article key={item.year} className="w-80 shrink-0 snap-start rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
              <p className="inline-block rounded-lg bg-navy-800 px-3 py-1 font-display text-sm font-bold text-gold-300">{item.year}</p>
              <ul className="mt-4 space-y-2 text-sm text-slate-700">
                {item.text
                  .split(",")
                  .map((t) => t.trim())
                  .filter(Boolean)
                  .slice(0, 4)
                  .map((t) => (
                    <li key={t} className="flex gap-2">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500" aria-hidden />
                      {t}
                    </li>
                  ))}
              </ul>
            </article>
          ))}
          <article className="flex w-72 shrink-0 snap-start flex-col justify-center rounded-2xl bg-navy-800 p-6 text-white">
            <p className="font-display text-sm font-bold text-gold-300">1996</p>
            <p className="mt-2 font-display text-2xl font-extrabold">Where it all began</p>
            <p className="mt-2 text-sm text-navy-100">TIS opened with grades 1 to 7 and received IB MYP authorisation in its first year.</p>
          </article>
        </div>
      </div>
    </section>
  );
}

function Events() {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    fetchEvents()
      .then(setEvents)
      .catch(() => setEvents([]));
  }, []);
  const now = new Date();
  const upcoming = (events || []).filter((e) => e.date && new Date(e.date) >= now);
  const list = (upcoming.length ? upcoming : events || []).slice(0, 5);
  return (
    <section className="bg-slate-50 py-20 md:py-24">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.1fr]">
        <div>
          <SectionHeading align="left" eyebrow="What's on" title={upcoming.length ? "Upcoming events" : "Recent events"} />
          <div className="mt-8 space-y-3">
            {events === null && <p className="text-slate-500">Loading events...</p>}
            {events && list.length === 0 && <p className="text-slate-500">New events will be announced soon.</p>}
            {list.map((e) => {
              const d = e.date ? new Date(e.date) : null;
              return (
                <div key={e.id} className="flex gap-4 rounded-2xl bg-white p-4 ring-1 ring-slate-200">
                  <div className="grid h-16 w-16 shrink-0 place-items-center rounded-xl bg-navy-800 text-center text-white">
                    {d ? (
                      <div>
                        <p className="font-display text-2xl font-extrabold leading-none">{d.getDate()}</p>
                        <p className="text-xs font-bold uppercase text-gold-300">{d.toLocaleString("en-GB", { month: "short" })}</p>
                      </div>
                    ) : (
                      <CalendarDays className="h-6 w-6" aria-hidden />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-display font-bold text-navy-800">{e.name}</p>
                    <p className="mt-1 flex items-center gap-1 text-sm text-slate-500">
                      <MapPin className="h-4 w-4 shrink-0" aria-hidden /> {e.location}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          <Link to="/gallery" className="mt-6 inline-flex items-center gap-2 font-bold text-navy-700 hover:gap-3">
            See the photo gallery <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <img src={eventImage1} alt="TIS event" className="row-span-2 h-full min-h-[18rem] w-full rounded-3xl object-cover" loading="lazy" />
          <img src={eventImage2} alt="TIS event" className="aspect-square w-full rounded-3xl object-cover" loading="lazy" />
          <img src={eventImage3} alt="TIS event" className="aspect-square w-full rounded-3xl object-cover" loading="lazy" />
        </div>
      </div>
    </section>
  );
}

function AdmissionsBand() {
  const steps = ["Create a parent account", "Fill in the application", "Upload documents & forms", "Assessment & interview"];
  return (
    <section className="bg-white py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="overflow-hidden rounded-[2rem] bg-gradient-to-br from-navy-800 to-navy-950 text-white shadow-lift">
          <div className="grid gap-10 p-8 sm:p-12 lg:grid-cols-2">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-gold-400">Admissions</p>
              <h2 className="mt-2 font-display text-3xl font-extrabold sm:text-4xl">We accept applications throughout the year</h2>
              <p className="mt-4 text-lg text-navy-100">
                For Primary, Middle and High School, in both the IB Continuum and Matriculation. Apply online and follow your
                application from your phone.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/admissions" className="inline-flex items-center gap-2 rounded-xl bg-gold-400 px-6 py-3.5 font-bold text-navy-900 hover:bg-gold-300">
                  Start your application <ArrowRight className="h-5 w-5" aria-hidden />
                </Link>
                <a
                  href={whatsappLink("Assalam o Alaikum, I would like information about admissions at TIS.")}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-6 py-3.5 font-bold text-white hover:bg-[#1ebe5a]"
                >
                  <WhatsAppIcon className="h-5 w-5" /> {SITE.phone}
                </a>
              </div>
            </div>
            <ol className="grid gap-4 sm:grid-cols-2">
              {steps.map((s, i) => (
                <li key={s} className="rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-gold-400 font-display font-extrabold text-navy-900">{i + 1}</span>
                  <p className="mt-3 font-display font-bold">{s}</p>
                </li>
              ))}
              <li className="sm:col-span-2">
                <Link to="/MeetTheAdmissionTeam" className="inline-flex items-center gap-2 text-sm font-semibold text-gold-300 hover:gap-3">
                  Meet the admissions team <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

function Counselling() {
  const questions = [
    "Choice of subject based on interest, career or profession",
    "Choice of college or university",
    "Choice of country or location",
    "Academic achievements",
    "Affordability",
    "Travel restrictions and considerations",
  ];
  return (
    <section className="grid bg-gold-400 lg:grid-cols-2">
      <img src={careerImage} alt="Career and university counselling at TIS" className="h-72 w-full object-cover lg:h-full" loading="lazy" />
      <div className="px-6 py-14 sm:px-12 lg:py-20">
        <p className="text-sm font-bold uppercase tracking-[0.18em] text-navy-700">Future ready</p>
        <h2 className="mt-2 font-display text-3xl font-extrabold text-navy-900 sm:text-4xl">Career &amp; university counselling</h2>
        <p className="mt-4 text-lg text-navy-900/80">
          One of the most important decisions students and parents make is about higher education. Our counsellors guide every
          student through:
        </p>
        <ul className="mt-5 grid gap-2 sm:grid-cols-2">
          {questions.map((q) => (
            <li key={q} className="flex gap-2 text-navy-900">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-navy-800" aria-hidden />
              {q}
            </li>
          ))}
        </ul>
        <Link to="/CareerAndUniCounseling" className="mt-8 inline-flex items-center gap-2 rounded-xl bg-navy-800 px-6 py-3.5 font-bold text-white hover:bg-navy-900">
          Read more <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <HeroSlider />
      <QuickLinks />
      <Programmes />
      <Stats />
      <Technology />
      <History />
      <AdmissionsBand />
      <Events />
      <Counselling />
      <Testimonial />
      <Map />
    </>
  );
}
